/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
import { TemplateExpressionScanner } from "./TemplateExpressionScanner";
import { TemplateExpressionTokenType } from "./TemplateExpressionTokenType";
var TemplateExpressionParser = /** @class */ (function () {
    function TemplateExpressionParser() {
    }
    TemplateExpressionParser.parseTemplateLanguageExpression = function (expression) {
        if (!TemplateExpressionParser.isTemplateLanguageExpression(expression)) {
            throw Error("The provided value ".concat(expression, " is not template language expression."));
        }
        if (expression[0] == "@" && expression[1] != "{") {
            if (expression[1] == "@") {
                throw Error("The provided template language expression ".concat(expression, " is invalid."));
            }
            return TemplateExpressionParser.ParseExpression(expression.substring(1));
        }
        return null;
    };
    TemplateExpressionParser.isTemplateLanguageExpression = function (expression) {
        if (!expression || expression.length < 2) {
            return false;
        }
        return expression[0] === "@" || expression.substr(0, 2) === "@{";
    };
    TemplateExpressionParser.ParseExpression = function (expression) {
        var scanner = new TemplateExpressionScanner(expression);
        var parsedExpression = TemplateExpressionParser.parseExpressionRecursive(scanner);
        scanner.expectToken(TemplateExpressionTokenType.EndOfData);
        return parsedExpression;
    };
    TemplateExpressionParser.parseExpressionRecursive = function (scanner) {
        var acceptedToken = scanner.tryAcceptToken(TemplateExpressionTokenType.Literal);
        if (acceptedToken.accepted) {
            return acceptedToken.token.Value;
        }
        acceptedToken = scanner.tryAcceptToken(TemplateExpressionTokenType.Integer);
        if (acceptedToken.accepted) {
            return acceptedToken.token.Value;
        }
        acceptedToken = scanner.tryAcceptToken(TemplateExpressionTokenType.Float);
        if (acceptedToken.accepted) {
            return acceptedToken.token.Value;
        }
        else if (scanner.tryAcceptIdentifier("null")) {
            return "";
        }
        else if (scanner.tryAcceptIdentifier("false")) {
            return false;
        }
        else if (scanner.tryAcceptIdentifier("true")) {
            return true;
        }
        else {
            return TemplateExpressionParser.parseTemplateFunctionExpression(scanner);
        }
    };
    TemplateExpressionParser.parseTemplateFunctionExpression = function (scanner) {
        var token = scanner.expectToken(TemplateExpressionTokenType.Identifier);
        var functionStartIndex = token.StartPosition;
        var functionName = token.Value;
        scanner.expectToken(TemplateExpressionTokenType.LeftParenthesis);
        var parameters = [];
        var acceptedToken = scanner.tryAcceptToken(TemplateExpressionTokenType.RightParenthesis);
        if (!acceptedToken.accepted) {
            do {
                parameters.push(TemplateExpressionParser.parseExpressionRecursive(scanner));
            } while (scanner.tryAcceptToken(TemplateExpressionTokenType.Comma).accepted);
            token = scanner.expectToken(TemplateExpressionTokenType.RightParenthesis);
        }
        var properties = [];
        while (true) {
            var dereference = {
                safeDereference: scanner.tryAcceptToken(TemplateExpressionTokenType.QuestionMark).accepted
            };
            if (scanner.tryAcceptToken(TemplateExpressionTokenType.Dot).accepted) {
                token = scanner.expectToken(TemplateExpressionTokenType.Identifier);
                dereference.expression = token.Value;
                properties.push(dereference);
                continue;
            }
            if (scanner.tryAcceptToken(TemplateExpressionTokenType.LeftSquareBracket).accepted) {
                dereference.expression = TemplateExpressionParser.parseExpressionRecursive(scanner);
                token = scanner.expectToken(TemplateExpressionTokenType.RightSquareBracket);
                properties.push(dereference);
                continue;
            }
            if (dereference.safeDereference) {
                throw new Error("Unexpected template dereference expression ".concat(dereference.expression, "."));
            }
            break;
        }
        return {
            expression: scanner.Expression,
            functionName: functionName,
            functionStartIndex: functionStartIndex,
            functionEndIndex: token.EndPosition,
            parameters: parameters,
            dereferences: properties
        };
    };
    return TemplateExpressionParser;
}());
export { TemplateExpressionParser };
